import { Alert, AlertIcon, Box, Input, Text } from "@chakra-ui/react";
import { ChangeEvent, FunctionComponent, ReactElement, useState } from "react";

// Components
import CustomModal from "../../shared/Modals/CustomModal";

interface ImportReportsModalProps {
	isOpen: boolean;
	onClose: () => void;
	onImport: (csvFile: File) => Promise<void> | void;
}

const normalizeCsvHeader = (header: string): string =>
	header
		.replace(/^\uFEFF/, "")
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");

const parseCsvLine = (line: string): Array<string> => {
	const fields: Array<string> = [];
	let field = "";
	let inQuotes = false;

	for (let i = 0; i < line.length; i += 1) {
		const currentChar = line[i];
		const nextChar = line[i + 1];

		if (currentChar === '"') {
			if (inQuotes && nextChar === '"') {
				field += '"';
				i += 1;
			} else {
				inQuotes = !inQuotes;
			}
		} else if (currentChar === "," && !inQuotes) {
			fields.push(field.trim());
			field = "";
		} else {
			field += currentChar;
		}
	}

	fields.push(field.trim());
	return fields;
};

const validateCsvHeaders = (rows: Array<string>): string | undefined => {
	const headers = parseCsvLine(rows[0]).map(normalizeCsvHeader);
	const requiredHeaders = ["claimid", "reportname", "reportedon", "type"];
	const missingHeaders = requiredHeaders.filter((requiredHeader) => !headers.includes(requiredHeader));

	if (missingHeaders.length === 0) {
		return undefined;
	}

	return `Missing required header(s): ${missingHeaders.join(", ")}.`;
};

const validateCsvForImport = async (csvFile: File): Promise<string | undefined> => {
	const rows = (await csvFile.text()).split(/\r?\n/).filter((row) => row.trim().length > 0);

	if (rows.length === 0) {
		return "The selected CSV file is empty.";
	}

	const headerError = validateCsvHeaders(rows);
	if (headerError) {
		return headerError;
	}

	return undefined;
};

const ImportReportsModal: FunctionComponent<ImportReportsModalProps> = (
	props: ImportReportsModalProps
): ReactElement => {
	const { isOpen, onClose, onImport } = props;

	const [csvFile, setCsvFile] = useState<File | null>(null);
	const [isValidating, setIsValidating] = useState<boolean>(false);
	const [validationError, setValidationError] = useState<string>("");

	const resetModalState = (): void => {
		setCsvFile(null);
		setValidationError("");
	};

	const handleClose = (): void => {
		if (isValidating) {
			return;
		}

		resetModalState();
		onClose();
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setCsvFile(event.target.files?.[0] ?? null);
		setValidationError("");
	};

	const handleImport = async (): Promise<void> => {
		if (!csvFile) {
			setValidationError("Please select a CSV file to import.");
			return;
		}

		setIsValidating(true);
		const selectedFile = csvFile;
		const foundValidationError = await validateCsvForImport(selectedFile);
		setIsValidating(false);

		if (foundValidationError) {
			setValidationError(foundValidationError);
			return;
		}

		handleClose();
		void onImport(selectedFile);
	};

	return (
		<CustomModal
			isOpen={isOpen}
			title="Import Reports"
			size="xl"
			content={
				<Box py={3}>
					<Text fontSize="sm" color="gray.700" mb={3}>
						Select a CSV file and click Import to process reports.
					</Text>
					<Input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
					{csvFile && (
						<Text mt={2} fontSize="sm" color="gray.700">
							Selected: {csvFile.name}
						</Text>
					)}

					{validationError && (
						<Alert status="error" mt={4} borderRadius="md">
							<AlertIcon />
							<Text fontSize="sm">{validationError}</Text>
						</Alert>
					)}
				</Box>
			}
			onContinue={() => void handleImport()}
			onCancel={isValidating ? undefined : handleClose}
			cancelText={isValidating ? null : "Close"}
			continueText="Import"
			continueLoading={isValidating}
			continueDisabled={!csvFile || isValidating}
		/>
	);
};

export default ImportReportsModal;
